import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Select from "react-select";
import { getUserData, encryptData } from "./const";
import {
  listAdminUsers,
  erpFetch,
  isErpApiSuccess,
  acceptAdminUser,
  rejectAdminUser,
} from "../erpClient";
import Pagination from "../components/Pagination";
import BranchAccessEditor, {
  toBranchAccessesPayload,
} from "../components/permissions/BranchAccessEditor";
import { hasPermission, PERMISSIONS } from "../utils/permissions";

import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { createErpSocket } from "../utils/socketIo";
// Status configuration
const STATUS_CONFIG = {
  0: {
    label: "Pending",
    badgeClass: "bg-warning-subtle text-warning",
  },
  1: {
    label: "Active",
    badgeClass: "bg-success-subtle text-success",
  },
  2: {
    label: "Inactive",
    badgeClass: "bg-danger-subtle text-danger",
  },
};

function formatEmployeeRoles(employee) {
  const roles = normalizeUserRoles(employee);
  if (!roles.length) return "—";
  return roles.map((r) => String(r)).join(", ");
}

const APPROVE_SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    borderRadius: "0.25rem",
    borderColor: "var(--bs-border-color, #dee2e6)",
    boxShadow: "none",
    "&:hover": { borderColor: "var(--bs-border-color, #dee2e6)" },
  }),
  menu: (base) => ({ ...base, zIndex: 1060 }),
};

function normalizeUserRoles(user) {
  if (Array.isArray(user?.roles)) {
    return user.roles.map((r) =>
      r && typeof r === "object" ? r.name ?? r.label ?? String(r.id ?? "") : r,
    ).filter(Boolean);
  }
  if (user?.role != null) {
    return typeof user.role === "object"
      ? [user.role.name ?? user.role.label].filter(Boolean)
      : [user.role];
  }
  return [];
}

// Avatar color variants
const AVATAR_COLORS = [
  "bg-primary",
  "bg-secondary",
  "bg-info",
  "bg-success",
  "bg-warning",
  "bg-danger",
];

// Move ConfirmationModal outside and memoize it
const ConfirmationModal = memo(
  ({
    show,
    onHide,
    onConfirm,
    title,
    message,
    buttonClass,
    contentClassName,
    confirmLoading = false,
  }) => {
    return (
      <Modal
        show={show}
        onHide={onHide}
        centered
        contentClassName={contentClassName}
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{message}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className={buttonClass}
            onClick={onHide}
            disabled={confirmLoading}
          >
            No
          </Button>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={onConfirm}
            disabled={confirmLoading}
          >
            {confirmLoading ? "Please wait..." : "Yes"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  },
);

function UserStatus() {
  const { buttonClass, cardClass, tableClass, buttonTableStyle } =
    useButtonTableStyle();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedModules, setExpandedModules] = useState({
    dailyprice: false,
    customer: false,
    bank: false,
    supplier: false,
    stock: false,
    sales: false,
    permission: false,
  });

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTargetEmployee, setRejectTargetEmployee] = useState(null);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTargetEmployee, setApproveTargetEmployee] = useState(null);
  const [approveBranchAccesses, setApproveBranchAccesses] = useState([]);
  const [tenantRows, setTenantRows] = useState([]);
  const [branchRows, setBranchRows] = useState([]);
  const [approveListsLoading, setApproveListsLoading] = useState(false);
  const [selectedTenantOption, setSelectedTenantOption] = useState(null);
  const [approveFormErrors, setApproveFormErrors] = useState({});
  const [isSavingApprove, setIsSavingApprove] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [userData, setUserData] = useState(getUserData()); // Make userData stateful for real-time updates
  const [permTick, setPermTick] = useState(0);
  const canManageEmployees = useMemo(
    () => hasPermission(PERMISSIONS.EMPLOYEE),
    [permTick],
  );

  useEffect(() => {
    const onPerms = () => setPermTick((t) => t + 1);
    window.addEventListener("permissionsUpdated", onPerms);
    return () => window.removeEventListener("permissionsUpdated", onPerms);
  }, []);

  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listAdminUsers();

      if (response?.success && Array.isArray(response.data?.users)) {
        const fetchedEmployees = response.data.users.map((user) => {
          return {
            ua_id: user.id,
            name: user.name || "",
            phone: user.phone || "",
            status: parseInt(user.status) || 0,
            email: user.email || "",
            roles: normalizeUserRoles(user),
            role_ids: Array.isArray(user.role_ids) ? user.role_ids : [],
          };
        });

        fetchedEmployees.sort((a, b) => b.ua_id - a.ua_id);
        setEmployees(fetchedEmployees);
      } else {
        console.error("Failed:", response?.message);
        toast.error(response?.message || "Failed to fetch employees");
      }
    } catch (error) {
      console.error("API error:", error);
      toast.error(error.response?.data?.message || "Failed to fetch employees");
    } finally {
      setIsLoading(false);
    }
  }, []);
  // Add useEffect to fetch data when component mounts
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in Employee, refetching employees...",
        event.detail,
      );
      fetchEmployees();
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [fetchEmployees]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("Employee: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("Employee: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("Employee: Socket connection error:", error);
    });

    newSocket.on("admin_user_updated", (data) => {
      // API emits `{ user }` from adminUserHandler; support legacy `{ employee }`.
      const payload = data?.user ?? data?.employee;
      if (!payload) return;

      const updatedEmployee = {
        ua_id: payload.id || payload.ua_id,
        name: payload.name || "",
        phone: payload.phone || "",
        status: parseInt(payload.status) || 0,
        email: payload.email || "",
        roles: normalizeUserRoles(payload),
        role_ids: Array.isArray(payload.role_ids) ? payload.role_ids : [],
      };

      // Check if the updated employee is the currently logged-in user
      if (
        updatedEmployee.ua_id === userData?.id ||
        updatedEmployee.ua_id === userData?.ua_id
      ) {
        const updatedUserData = {
          ...userData,
          status: updatedEmployee.status,
          roles: updatedEmployee.roles,
        };

        setUserData(updatedUserData);

        try {
          const currentUserData = getUserData();
          if (currentUserData) {
            const updatedStorageData = {
              ...currentUserData,
              status: updatedEmployee.status,
              roles: updatedEmployee.roles,
            };
            localStorage.setItem("userData", encryptData(updatedStorageData));
          }
        } catch (error) {
          console.error("Error updating stored user data:", error);
        }

        toast.success("Your account details were updated.");
      }

      setEmployees((prev) => {
        // Check if employee exists (update case)
        const exists = prev.some((emp) => emp.ua_id === updatedEmployee.ua_id);
        if (exists) {
          return prev.map((emp) =>
            emp.ua_id === updatedEmployee.ua_id
              ? {
                  ...emp,
                  ...updatedEmployee,
                  roles:
                    updatedEmployee.roles?.length > 0
                      ? updatedEmployee.roles
                      : emp.roles || [],
                }
              : emp,
          );
        }
        // Add new employee if it doesn't exist
        return [updatedEmployee, ...prev].sort((a, b) => b.ua_id - a.ua_id);
      });
    });

    return () => {
      newSocket.off("admin_user_updated");
      newSocket.disconnect();
    };
  }, []);

  // Filter employees based on status and search query
  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch = employee.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "" || employee.status.toString() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);
  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
  };

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    } else if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Helper function to get user initials
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  // Helper function to get avatar color
  const getAvatarColor = (index) => {
    return AVATAR_COLORS[index % AVATAR_COLORS.length];
  };

  // Handle edit action
  const handleEdit = (userId) => {
    navigate("/editemployee", { state: { userId: userId } });
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const toggleModule = (moduleName) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleName]: !prev[moduleName],
    }));
  };

  // Memoize handlers with useCallback
  const resetApproveForm = useCallback(() => {
    setApproveBranchAccesses([
      {
        key: `new-${Date.now()}`,
        branch_id: "",
        role_ids: [],
        permission_codes: [],
        role_default_codes: [],
        is_default: true,
      },
    ]);
    setSelectedTenantOption(null);
    setApproveFormErrors({});
  }, []);

  const handleApprove = useCallback(
    (employee) => {
      setApproveTargetEmployee(employee);
      resetApproveForm();
      setShowApproveModal(true);
    },
    [resetApproveForm],
  );

  const handleReject = useCallback((employee) => {
    setRejectTargetEmployee(employee);
    setShowRejectModal(true);
  }, []);

  const handleCloseApproveModal = useCallback(() => {
    setShowApproveModal(false);
    setApproveTargetEmployee(null);
    resetApproveForm();
  }, [resetApproveForm]);

  useEffect(() => {
    if (!showApproveModal) return;

    let cancelled = false;
    const loadLists = async () => {
      setApproveListsLoading(true);
      try {
        const [tenantRes, branchRes] = await Promise.all([
          erpFetch("/tenant", { method: "POST", body: { type: "list" } }),
          erpFetch("/branch", { method: "POST", body: { type: "list" } }),
        ]);
        if (cancelled) return;

        if (
          isErpApiSuccess(tenantRes) &&
          Array.isArray(tenantRes?.data?.tenants)
        ) {
          setTenantRows(
            tenantRes.data.tenants.map((t) => ({ id: t.id, name: t.name })),
          );
        } else {
          setTenantRows([]);
          if (!isErpApiSuccess(tenantRes)) {
            toast.error(tenantRes?.message || "Could not load tenants.");
          }
        }

        if (
          isErpApiSuccess(branchRes) &&
          Array.isArray(branchRes?.data?.branches)
        ) {
          setBranchRows(
            branchRes.data.branches.map((b) => ({
              id: b.id,
              tenant_id: b.tenant_id,
              name: b.name,
              code: b.code,
            })),
          );
        } else {
          setBranchRows([]);
          if (!isErpApiSuccess(branchRes)) {
            toast.error(branchRes?.message || "Could not load branches.");
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast.error("Failed to load tenant or branch lists.");
          setTenantRows([]);
          setBranchRows([]);
        }
      } finally {
        if (!cancelled) setApproveListsLoading(false);
      }
    };

    loadLists();
    return () => {
      cancelled = true;
    };
  }, [showApproveModal]);

  const tenantOptions = useMemo(
    () =>
      [...tenantRows]
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        .map((t) => ({ value: t.id, label: t.name })),
    [tenantRows],
  );

  const approveBranchOptions = useMemo(() => {
    const tid = selectedTenantOption?.value;
    if (tid == null) return [];
    return branchRows
      .filter((b) => Number(b.tenant_id) === Number(tid))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      .map((b) => ({
        value: b.id,
        id: b.id,
        label: b.code ? `${b.name} (${b.code})` : b.name,
        name: b.name,
      }));
  }, [branchRows, selectedTenantOption]);

  const handleSaveApprove = useCallback(async () => {
    if (!approveTargetEmployee) return;

    const nextErrors = {};
    if (!selectedTenantOption) nextErrors.tenant = "Select a tenant";
    const payloadAccesses = toBranchAccessesPayload(approveBranchAccesses);
    if (!payloadAccesses.length) {
      nextErrors.access = "Add at least one branch access";
    } else if (!payloadAccesses.some((r) => r.is_default)) {
      nextErrors.access = "Mark one branch as default";
    } else if (payloadAccesses.some((r) => !r.role_ids?.length)) {
      nextErrors.access = "Each branch access needs a role";
    }
    setApproveFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const defaultRow = payloadAccesses.find((r) => r.is_default);

    setIsSavingApprove(true);
    try {
      const res = await acceptAdminUser({
        id: approveTargetEmployee.ua_id,
        tenant_id: selectedTenantOption.value,
        branch_accesses: payloadAccesses,
        branch_id: defaultRow?.branch_id,
        role_ids: defaultRow?.role_ids,
        permission_codes: defaultRow?.permission_codes,
      });

      if (res?.success || isErpApiSuccess(res)) {
        toast.success(res?.message || "User approved successfully");
        handleCloseApproveModal();
        await fetchEmployees();
      } else {
        toast.error(res?.message || "Approve failed");
      }
    } catch (error) {
      console.error("Approve error:", error);
      toast.error(
        error.response?.data?.message || "Failed to approve employee",
      );
    } finally {
      setIsSavingApprove(false);
    }
  }, [
    approveTargetEmployee,
    approveBranchAccesses,
    selectedTenantOption,
    handleCloseApproveModal,
    fetchEmployees,
  ]);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectTargetEmployee) return;

    setIsRejecting(true);
    try {
      const res = await rejectAdminUser({
        id: rejectTargetEmployee.ua_id,
      });

      if (res?.success || isErpApiSuccess(res)) {
        toast.success(res?.message || "User rejected");
        setShowRejectModal(false);
        setRejectTargetEmployee(null);
        await fetchEmployees();
      } else {
        toast.error(res?.message || "Reject failed");
      }
    } catch (error) {
      console.error("Reject error:", error);
      toast.error(error.response?.data?.message || "Failed to reject employee");
    } finally {
      setIsRejecting(false);
    }
  }, [rejectTargetEmployee, fetchEmployees]);

  const handleCloseRejectModal = useCallback(() => {
    setShowRejectModal(false);
    setRejectTargetEmployee(null);
  }, []);

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom">
            <h4 className="card-title mb-0">Employee Table</h4>
          </div>
          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${
                  buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                className={`form-select w-auto ms-3 ${
                  buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="">All Status</option>
                <option value="0">Pending</option>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>
                      <h6 className="fs-4 fw-semibold mb-0">Name</h6>
                    </th>
                    <th>
                      <h6 className="fs-4 fw-semibold mb-0">Mobile Number</h6>
                    </th>
                    <th>
                      <h6 className="fs-4 fw-semibold mb-0">Role</h6>
                    </th>
                    <th>
                      <h6 className="fs-4 fw-semibold mb-0">Status</h6>
                    </th>
                    <th>
                      <h6 className="fs-4 fw-semibold mb-0">Actions</h6>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : (
                    currentEmployees.map((employee) => {
                      const statusConfig = STATUS_CONFIG[employee.status];
                      const roleLabel = formatEmployeeRoles(employee);

                      return (
                        <tr key={employee.ua_id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="ms-3">
                                <h6 className="fs-4 fw-semibold mb-0">
                                  {employee.name}
                                </h6>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="fw-normal">{employee.phone}</span>
                          </td>
                          <td>
                            {employee.status === 0 ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <span className="badge bg-info-subtle text-info px-3 py-2">
                                {roleLabel}
                              </span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`badge ${statusConfig.badgeClass} px-3 py-2`}
                            >
                              {statusConfig.label}
                            </span>
                          </td>
                          <td>
                            {canManageEmployees ? (
                              employee.status === 0 ? (
                                <div className="d-flex gap-2">
                                  <button
                                    className={`btn btn-success btn-sm ${buttonClass}`}
                                    aria-label={`Approve ${employee.name}`}
                                    onClick={() => handleApprove(employee)}
                                  >
                                    <Icon
                                      icon="solar:check-circle-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </button>
                                  <button
                                    className={`btn btn-danger btn-sm ${buttonClass}`}
                                    aria-label={`Reject ${employee.name}`}
                                    onClick={() => handleReject(employee)}
                                  >
                                    <Icon
                                      icon="solar:close-circle-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => handleEdit(employee.ua_id)}
                                  aria-label={`Edit ${employee.name}`}
                                >
                                  <Icon
                                    icon="solar:pen-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                              )
                            ) : (
                              <span className="text-muted">
                                <Icon
                                  icon="solar:lock-bold"
                                  width="16"
                                  height="16"
                                  style={{ opacity: 0.5 }}
                                />
                                <small className="ms-1">No Permission</small>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredEmployees.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      <Modal
        show={showApproveModal}
        onHide={() => {
          if (!isSavingApprove) handleCloseApproveModal();
        }}
        backdrop={isSavingApprove ? "static" : true}
        keyboard={!isSavingApprove}
        centered
        size="lg"
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Header closeButton={!isSavingApprove}>
          <Modal.Title>Approve Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {approveTargetEmployee && (
            <p className="text-muted small mb-3">
              Approving <strong>{approveTargetEmployee.name}</strong> (
              {approveTargetEmployee.phone})
            </p>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Tenant</Form.Label>
            <Select
              inputId="approve-tenant"
              options={tenantOptions}
              value={selectedTenantOption}
              onChange={(opt) => {
                setSelectedTenantOption(opt);
                setApproveBranchAccesses([
                  {
                    key: `new-${Date.now()}`,
                    branch_id: "",
                    role_ids: [],
                    permission_codes: [],
                    role_default_codes: [],
                    is_default: true,
                  },
                ]);
                setApproveFormErrors((prev) => ({
                  ...prev,
                  tenant: undefined,
                  access: undefined,
                }));
              }}
              placeholder={
                approveListsLoading
                  ? "Loading tenants..."
                  : "Search or select tenant"
              }
              isSearchable
              isClearable
              isDisabled={approveListsLoading || tenantOptions.length === 0}
              styles={APPROVE_SELECT_STYLES}
              classNamePrefix="react-select"
            />
            {approveFormErrors.tenant && (
              <div className="text-danger small mt-1">
                {approveFormErrors.tenant}
              </div>
            )}
          </Form.Group>
          <div className="border-top pt-3">
            <h6 className="mb-2">Branch access</h6>
            <p className="text-muted small mb-2">
              Select tenant first, then add one or more branches with roles and
              permission ticks. Mark one default.
            </p>
            {!selectedTenantOption ? (
              <p className="text-muted small mb-0">Select a tenant to assign branches.</p>
            ) : (
              <BranchAccessEditor
                value={approveBranchAccesses}
                onChange={setApproveBranchAccesses}
                branchOptions={approveBranchOptions}
                square={buttonTableStyle === "square"}
                compact
              />
            )}
            {approveFormErrors.access && (
              <div className="text-danger small mt-1">
                {approveFormErrors.access}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className={buttonClass}
            onClick={handleCloseApproveModal}
            disabled={isSavingApprove}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveApprove}
            disabled={isSavingApprove || approveListsLoading}
          >
            {isSavingApprove ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        buttonClass={buttonClass}
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
        show={showRejectModal}
        onHide={() => {
          if (!isRejecting) handleCloseRejectModal();
        }}
        onConfirm={handleRejectConfirm}
        confirmLoading={isRejecting}
        title="Reject Employee"
        message="By rejecting, the user will be permanently removed and will not be able to access the software. Are you sure you want to proceed?"
      />
    </div>
  );
}

export default UserStatus;
